/**
 * Dashboard de BI — métricas, gráficos e ranking de vendedores.
 *
 * Consome `GET /propostas` e apresenta visualizações agregadas.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { pode } from '@emaster/dominio';

import { listarPropostas } from '../api/cliente.ts';
import { useAutenticacao } from '../autenticacao/contexto.tsx';
import {
  agruparAcompanhamento,
  FAIXAS,
  type GrupoFaixa,
  type FaixaAcompanhamento,
} from './carteiraAcompanhamento.ts';
import {
  calcularIntervalo,
  chaveTrimestre,
  descreverPeriodo,
  filtrarPorIntervalo,
  formatarDataIso,
  GRANULARIDADE_SUGERIDA,
  intervaloDoTrimestre,
  PERIODO_PADRAO,
  ROTULOS_PERIODO,
  rotuloTrimestre,
  trimestreCorrente,
  trimestreDoMes,
  trimestresDisponiveis,
  type TipoPeriodo,
  type VisaoEnvios,
} from './periodoDashboard.ts';

// ---------------------------------------------------------------------------
// Paleta de cores para gráficos
// ---------------------------------------------------------------------------

const CORES_GRAFICO: readonly string[] = [
  '#0A2540', // azul institucional
  '#9E0B3B', // vermelho eMaster
  '#F97316', // laranja destaque
  '#143C66', // azul claro
  '#C41E4A', // vermelho claro
  '#FB923C', // laranja claro
  '#1E5185', // azul médio
  '#7A0A2E', // vermelho escuro
  '#EA580C', // laranja escuro
  '#2D6BA3', // azul suave
  '#5D7A90', // cinza azulado
  '#D97706', // âmbar
];

// ---------------------------------------------------------------------------
// Tipos da resposta
// ---------------------------------------------------------------------------

/**
 * Resumo financeiro da proposta como a API o devolve.
 *
 * Dois valores de mensalidade, de propósito, porque respondem a perguntas diferentes:
 *
 * - `mensalidadeUsd` é o **faturado** durante a janela do crédito. É o número do cliente, e é o que o
 *   detalhe de cada proposta mostra, ao lado do crédito que o produziu.
 * - `mensalidadeSemCreditoUsd` é a **recorrência** antes do incentivo. É o número do gestor, e é o que
 *   as métricas agregadas desta tela usam.
 *
 * Misturar os dois é o defeito que esta distinção corrige: somar o faturado dava a um vendedor com
 * carteira de milhares um volume de US$ 15,66, porque o crédito tinha zerado a parcela.
 */
interface Financeiro {
  readonly mensalidadeUsd: number;
  readonly mensalidadeBrl: number | null;
  readonly mensalidadeSemCreditoUsd: number;
  readonly consumoUsd: number;
  readonly suporteUsd: number;
  readonly deployUsd: number;
  readonly creditoUsd: number;
  readonly creditoJanelaMeses: number;
  readonly economiaPercentual: number;
  readonly margemPercentual: number;
  readonly planoSuporte: string;
  readonly trilha: string;
  readonly duracaoMeses: number;
}

interface ItemLista {
  readonly numero: string;
  readonly situacao: string;
  readonly cliente: string;
  readonly projeto: string;
  readonly responsavelEmail: string;
  readonly responsavelSub: string;
  readonly emitidaEm: string;
  readonly validaAte: string;
  readonly versao: number;
  readonly url: string;
  readonly financeiro: Financeiro | null;
  readonly legado: boolean;
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** Formata um valor numérico como USD no padrão brasileiro: US$ 1.234,56 */
function formatarUsd(v: number): string {
  return 'US$ ' + v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Extrai nome legível a partir do email. */
function extrairNome(email: string): string {
  return (
    email
      .split('@')[0]
      ?.split('.')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ') ?? email
  );
}

// ---------------------------------------------------------------------------
// Helpers de período
// ---------------------------------------------------------------------------

/** Retorna o número da semana ISO de uma data (string AAAA-MM-DD). */
function semanaISO(dataStr: string): number {
  const d = new Date(dataStr + 'T00:00:00');
  const temp = new Date(d.getTime());
  temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7));
  const semana1 = new Date(temp.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((temp.getTime() - semana1.getTime()) / 86400000 - 3 + ((semana1.getDay() + 6) % 7)) / 7,
    )
  );
}

/** Retorna o ano ISO correspondente à semana ISO de uma data. */
function anoISO(dataStr: string): number {
  const d = new Date(dataStr + 'T00:00:00');
  const temp = new Date(d.getTime());
  temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7));
  return temp.getFullYear();
}

// ---------------------------------------------------------------------------
// Constantes do seletor de período — ordem no <select>
// ---------------------------------------------------------------------------

/** Ordem fixa das opções no seletor, para o JSX não depender da ordem das chaves do Record. */
const OPCOES_PERIODO: readonly TipoPeriodo[] = [
  'hoje',
  'semanaAtual',
  'mesAtual',
  'trimestre',
  'anoAtual',
  'tudo',
  'personalizado',
];

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function TelaDashboard() {
  const { sessao } = useAutenticacao();
  const veTodas = pode(sessao?.perfil ?? null, 'ver_todas');

  const [todosItens, setTodosItens] = useState<readonly ItemLista[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [top5Expandido, setTop5Expandido] = useState<string | null>(null);
  const [comoUsuario, setComoUsuario] = useState('');

  // Período global
  const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodo>(PERIODO_PADRAO);
  const [dataDe, setDataDe] = useState('');
  const [dataAte, setDataAte] = useState('');
  const [trimestreSelecionado, setTrimestreSelecionado] = useState('');

  // Filtros dos gráficos
  const [visaoEnvios, setVisaoEnvios] = useState<VisaoEnvios>(
    GRANULARIDADE_SUGERIDA[PERIODO_PADRAO],
  );
  const [mesFiltroVendedor, setMesFiltroVendedor] = useState('');

  // Filtro do bloco "Acompanhamento da carteira" (vendedor sem ver_todas)
  const [filtroFaixaCarteira, setFiltroFaixaCarteira] = useState<FaixaAcompanhamento | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      const r = await listarPropostas();

      if (!r.ok) {
        const dados = r.dados as { mensagem?: string };
        setErro(
          dados.mensagem ?? `Não foi possível carregar as propostas (HTTP ${String(r.status)}).`,
        );
        return;
      }

      const dados = r.dados as { itens?: readonly ItemLista[] };
      setTodosItens(dados.itens ?? []);
      setAtualizadoEm(new Date());
    } catch (e) {
      setErro(
        `Falha na comunicação com o servidor: ${e instanceof Error ? e.message : 'erro desconhecido'}`,
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // ---------------------------------------------------------------------------
  // Filtro de período global + comoUsuario
  // ---------------------------------------------------------------------------

  /**
   * Intervalo efetivo, em `AAAA-MM-DD`, para comparar direto com `emitidaEm`, que é string no mesmo
   * formato. Comparar string evita fuso horário: `new Date('2026-08-14')` é meia-noite UTC, que no
   * Brasil é dia 13 às 21h, e proposta emitida hoje sumiria do filtro "hoje".
   */
  const intervalo = useMemo(
    () => calcularIntervalo(tipoPeriodo, dataDe, dataAte, new Date(), trimestreSelecionado),
    [tipoPeriodo, dataDe, dataAte, trimestreSelecionado],
  );

  /** Trimestre futuro: intervalo null quando tipo é 'trimestre'. */
  const trimestreFuturo = tipoPeriodo === 'trimestre' && intervalo === null;

  /**
   * Itens filtrados por usuário e período. Substituiu o `useEffect` + `setItens` anterior porque
   * um valor derivado não precisa de estado — eliminando um render extra a cada mudança.
   */
  const itens = useMemo(() => {
    if (trimestreFuturo) return [] as readonly ItemLista[];
    let base = todosItens;
    if (comoUsuario !== '') base = base.filter((i) => i.responsavelSub === comoUsuario);
    return filtrarPorIntervalo(base, intervalo);
  }, [todosItens, comoUsuario, intervalo, trimestreFuturo]);

  /** Descrição do período ativo, para os títulos e cards não deixarem dúvida sobre a janela. */
  const descricaoPeriodo = useMemo(
    () => descreverPeriodo(tipoPeriodo, intervalo, new Date(), trimestreSelecionado),
    [tipoPeriodo, intervalo, trimestreSelecionado],
  );

  /** Data de hoje em ISO para limitar os inputs de data. */
  const hojeIso = useMemo(() => formatarDataIso(new Date()), []);

  /** Aviso quando o campo "De" é posterior ao campo "Até". */
  const periodoInvertido = dataDe !== '' && dataAte !== '' && dataDe > dataAte;

  // Lista de responsáveis derivada de todos os itens
  const responsaveis = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const item of todosItens) {
      if (!mapa.has(item.responsavelSub)) {
        mapa.set(item.responsavelSub, extrairNome(item.responsavelEmail));
      }
    }
    return [...mapa.entries()]
      .map(([sub, nome]) => ({ sub, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [todosItens]);

  // ---------------------------------------------------------------------------
  // Trimestres disponíveis para o seletor secundário (baseado em todosItens)
  // ---------------------------------------------------------------------------

  const opcoesTrimestreDisponiveis = useMemo(
    () => trimestresDisponiveis(todosItens, new Date()),
    [todosItens],
  );

  // ---------------------------------------------------------------------------
  // Botões de atalho Q1-Q4 do ano corrente
  // ---------------------------------------------------------------------------

  const anoCorrente = new Date().getFullYear();
  const trimAtivo =
    trimestreSelecionado === '' ? trimestreCorrente(new Date()) : trimestreSelecionado;

  // ---------------------------------------------------------------------------
  // Métricas
  // ---------------------------------------------------------------------------

  const metricas = useMemo(() => {
    let volumeTotal = 0;
    let creditosTotal = 0;
    let maiorMensalidade = 0;
    let quantidade = 0;
    let legadoCount = 0;

    /*
     * As agregações usam a mensalidade **sem** o crédito. Leitura gerencial quer a recorrência que
     * sobra quando o incentivo termina, não a parcela promocional: o crédito é temporário e ainda
     * depende de aprovação da AWS.
     *
     * `creditosTotal` continua vindo do crédito pleiteado, e agora é a contrapartida do volume em vez
     * de estar embutido nele — os dois números juntos passam a contar a história inteira.
     */
    for (const item of itens) {
      if (item.legado) legadoCount += 1;
      if (item.financeiro !== null) {
        const recorrencia = item.financeiro.mensalidadeSemCreditoUsd;
        volumeTotal += recorrencia;
        creditosTotal += item.financeiro.creditoUsd;
        if (recorrencia > maiorMensalidade) {
          maiorMensalidade = recorrencia;
        }
        quantidade += 1;
      }
    }

    return {
      total: itens.length,
      legadoCount,
      comValor: quantidade,
      volumeTotal,
      ticketMedio: quantidade > 0 ? volumeTotal / quantidade : 0,
      creditosTotal,
      maiorMensalidade,
    };
  }, [itens]);

  // ---------------------------------------------------------------------------
  // Top 5 propostas por valor
  // ---------------------------------------------------------------------------

  const top5 = useMemo(() => {
    return itens
      .filter((item) => item.financeiro !== null)
      .map((item) => ({
        item,
        valor: item.financeiro?.mensalidadeSemCreditoUsd ?? 0,
      }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 5);
  }, [itens]);

  // ---------------------------------------------------------------------------
  // Rank de vendedores
  // ---------------------------------------------------------------------------

  const rankVendedores = useMemo(() => {
    const mapa = new Map<string, { quantidade: number; volume: number }>();

    for (const item of itens) {
      const email = item.responsavelEmail;
      const entrada = mapa.get(email) ?? { quantidade: 0, volume: 0 };
      entrada.quantidade += 1;
      entrada.volume += item.financeiro !== null ? item.financeiro.mensalidadeSemCreditoUsd : 0;
      mapa.set(email, entrada);
    }

    return [...mapa.entries()]
      .map(([email, dados]) => ({
        nome: extrairNome(email),
        quantidade: dados.quantidade,
        volume: dados.volume,
        ticketMedio: dados.volume > 0 ? dados.volume / dados.quantidade : 0,
      }))
      .sort((a, b) => b.volume - a.volume || b.quantidade - a.quantidade);
  }, [itens]);

  // ---------------------------------------------------------------------------
  // Meses disponíveis (para selects)
  // ---------------------------------------------------------------------------

  const mesesDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const item of itens) {
      set.add(item.emitidaEm.slice(0, 7));
    }
    return [...set].sort().reverse();
  }, [itens]);

  // Inicializa selects quando meses ficam disponíveis
  useEffect(() => {
    if (mesesDisponiveis.length > 0) {
      if (!mesFiltroVendedor) setMesFiltroVendedor(mesesDisponiveis[0] ?? '');
    }
  }, [mesesDisponiveis, mesFiltroVendedor]);

  // ---------------------------------------------------------------------------
  // Envios por período — agora com 5 visões, respeitando o intervalo global
  // ---------------------------------------------------------------------------

  const TETO_COLUNAS_GRAFICO = 60;

  const dadosEvolucao = useMemo(() => {
    const hoje = new Date();

    if (visaoEnvios === 'diaria') {
      const limiteStr =
        intervalo !== null
          ? intervalo.de
          : (() => {
              const limite = new Date(hoje);
              limite.setDate(limite.getDate() - 30);
              return limite.toISOString().slice(0, 10);
            })();
      const ateStr = intervalo !== null ? intervalo.ate : hoje.toISOString().slice(0, 10);

      const mapa = new Map<string, number>();
      for (const item of itens) {
        if (item.emitidaEm >= limiteStr && item.emitidaEm <= ateStr) {
          mapa.set(item.emitidaEm, (mapa.get(item.emitidaEm) ?? 0) + 1);
        }
      }

      const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
      const cursor = new Date(limiteStr + 'T00:00:00');
      const fim = new Date(ateStr + 'T00:00:00');
      while (cursor <= fim) {
        const chave = cursor.toISOString().slice(0, 10);
        const dia = chave.slice(8, 10);
        resultado.push({ chave, rotulo: dia, quantidade: mapa.get(chave) ?? 0 });
        cursor.setDate(cursor.getDate() + 1);
      }

      if (resultado.length > TETO_COLUNAS_GRAFICO) {
        return resultado.slice(-TETO_COLUNAS_GRAFICO);
      }
      return resultado;
    }

    if (visaoEnvios === 'semanal') {
      const limiteStr =
        intervalo !== null
          ? intervalo.de
          : (() => {
              const limite = new Date(hoje);
              limite.setDate(limite.getDate() - 12 * 7);
              return limite.toISOString().slice(0, 10);
            })();
      const ateStr = intervalo !== null ? intervalo.ate : hoje.toISOString().slice(0, 10);

      const mapa = new Map<string, number>();
      for (const item of itens) {
        if (item.emitidaEm >= limiteStr && item.emitidaEm <= ateStr) {
          const sem = semanaISO(item.emitidaEm);
          const ano = anoISO(item.emitidaEm);
          const chave = `${String(ano)}-W${String(sem).padStart(2, '0')}`;
          mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
        }
      }

      const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
      const cursor = new Date(limiteStr + 'T00:00:00');
      const fim = new Date(ateStr + 'T00:00:00');
      const semanasVistas = new Set<string>();
      while (cursor <= fim) {
        const dStr = cursor.toISOString().slice(0, 10);
        const sem = semanaISO(dStr);
        const ano = anoISO(dStr);
        const chave = `${String(ano)}-W${String(sem).padStart(2, '0')}`;
        if (!semanasVistas.has(chave)) {
          semanasVistas.add(chave);
          resultado.push({ chave, rotulo: `Sem ${String(sem)}`, quantidade: mapa.get(chave) ?? 0 });
        }
        cursor.setDate(cursor.getDate() + 1);
      }

      if (resultado.length > TETO_COLUNAS_GRAFICO) {
        return resultado.slice(-TETO_COLUNAS_GRAFICO);
      }
      return resultado;
    }

    if (visaoEnvios === 'trimestral') {
      const mapa = new Map<string, number>();
      for (const item of itens) {
        const [anoStr, mesStr] = item.emitidaEm.split('-');
        const mes = parseInt(mesStr ?? '1', 10);
        const t = trimestreDoMes(mes);
        const chave = `${anoStr ?? ''}-Q${String(t)}`;
        mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
      }

      if (intervalo !== null) {
        const [anoDeStr, mesDeStr] = intervalo.de.split('-');
        const [anoAteStr, mesAteStr] = intervalo.ate.split('-');
        const anoDe = parseInt(anoDeStr ?? '2025', 10);
        const mesDe = parseInt(mesDeStr ?? '1', 10);
        const anoAte = parseInt(anoAteStr ?? '2026', 10);
        const mesAte = parseInt(mesAteStr ?? '12', 10);

        const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
        let anoC = anoDe;
        let tC = trimestreDoMes(mesDe);
        const tFim = trimestreDoMes(mesAte);

        while (anoC < anoAte || (anoC === anoAte && tC <= tFim)) {
          const chave = `${String(anoC)}-Q${String(tC)}`;
          const rotulo = `Q${String(tC)}/${String(anoC).slice(2)}`;
          resultado.push({ chave, rotulo, quantidade: mapa.get(chave) ?? 0 });
          tC += 1;
          if (tC > 4) {
            tC = 1;
            anoC += 1;
          }
        }

        if (resultado.length > TETO_COLUNAS_GRAFICO) {
          return resultado.slice(-TETO_COLUNAS_GRAFICO);
        }
        return resultado;
      }

      // Sem intervalo: últimos 8 trimestres
      const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
      let anoAtual = hoje.getFullYear();
      let tAtual = trimestreDoMes(hoje.getMonth() + 1);
      for (let i = 0; i < 8; i++) {
        const chave = `${String(anoAtual)}-Q${String(tAtual)}`;
        const rotulo = `Q${String(tAtual)}/${String(anoAtual).slice(2)}`;
        resultado.unshift({ chave, rotulo, quantidade: mapa.get(chave) ?? 0 });
        tAtual -= 1;
        if (tAtual === 0) {
          tAtual = 4;
          anoAtual -= 1;
        }
      }
      return resultado;
    }

    if (visaoEnvios === 'anual') {
      const mapa = new Map<string, number>();
      for (const item of itens) {
        const ano = item.emitidaEm.slice(0, 4);
        mapa.set(ano, (mapa.get(ano) ?? 0) + 1);
      }

      let primeiro: number;
      let ultimo: number;

      if (intervalo !== null) {
        primeiro = parseInt(intervalo.de.slice(0, 4), 10);
        ultimo = parseInt(intervalo.ate.slice(0, 4), 10);
        if (primeiro < 2000)
          primeiro = Math.min(
            ...[...mapa.keys()].map(Number).filter((n) => !Number.isNaN(n)),
            hoje.getFullYear(),
          );
        if (ultimo > hoje.getFullYear() + 10) ultimo = hoje.getFullYear();
      } else {
        const anos = [...mapa.keys()].map((a) => parseInt(a, 10)).filter((a) => !Number.isNaN(a));
        if (anos.length === 0) return [];
        primeiro = Math.min(...anos);
        ultimo = Math.max(Math.max(...anos), hoje.getFullYear());
      }

      const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
      for (let a = primeiro; a <= ultimo; a++) {
        const chave = String(a);
        resultado.push({ chave, rotulo: chave, quantidade: mapa.get(chave) ?? 0 });
      }

      if (resultado.length > TETO_COLUNAS_GRAFICO) {
        return resultado.slice(-TETO_COLUNAS_GRAFICO);
      }
      return resultado;
    }

    // mensal
    const mapa = new Map<string, number>();
    for (const item of itens) {
      const chave = item.emitidaEm.slice(0, 7);
      mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
    }

    if (intervalo !== null) {
      const [anoDeStr, mesDeStr] = intervalo.de.split('-');
      const [anoAteStr, mesAteStr] = intervalo.ate.split('-');
      let anoDe = parseInt(anoDeStr ?? '2025', 10);
      let mesDe = parseInt(mesDeStr ?? '1', 10);
      const anoAte = parseInt(anoAteStr ?? '2026', 10);
      const mesAte = parseInt(mesAteStr ?? '12', 10);

      if (anoDe < 2000) {
        const chavesPresentes = [...mapa.keys()].sort();
        const maisAntiga = chavesPresentes[0];
        if (maisAntiga !== undefined) {
          const [aStr, mStr] = maisAntiga.split('-').map(Number);
          anoDe = aStr ?? 2025;
          mesDe = mStr ?? 1;
        }
      }

      const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
      let anoC = anoDe;
      let mesC = mesDe;
      while (anoC < anoAte || (anoC === anoAte && mesC <= mesAte)) {
        const chave = `${String(anoC)}-${String(mesC).padStart(2, '0')}`;
        const rotulo = `${MESES[mesC - 1] ?? ''}/${String(anoC).slice(2)}`;
        resultado.push({ chave, rotulo, quantidade: mapa.get(chave) ?? 0 });
        mesC += 1;
        if (mesC > 12) {
          mesC = 1;
          anoC += 1;
        }
      }

      if (resultado.length > TETO_COLUNAS_GRAFICO) {
        return resultado.slice(-TETO_COLUNAS_GRAFICO);
      }
      return resultado;
    }

    const chavesPresentes = [...mapa.keys()].sort();
    const maisAntiga = chavesPresentes[0];

    let meses = 12;
    if (maisAntiga !== undefined) {
      const [anoAntigo, mesAntigo] = maisAntiga.split('-').map(Number);
      if (anoAntigo !== undefined && mesAntigo !== undefined) {
        const distancia =
          (hoje.getFullYear() - anoAntigo) * 12 + (hoje.getMonth() + 1 - mesAntigo) + 1;
        meses = Math.min(Math.max(distancia, 6), 18);
      }
    }

    const resultado: { chave: string; rotulo: string; quantidade: number }[] = [];
    let anoAtual = hoje.getFullYear();
    let mesAtual = hoje.getMonth() + 1;
    for (let i = 0; i < meses; i++) {
      const chave = `${String(anoAtual)}-${String(mesAtual).padStart(2, '0')}`;
      const rotulo = `${MESES[mesAtual - 1] ?? ''}/${String(anoAtual).slice(2)}`;
      resultado.unshift({ chave, rotulo, quantidade: mapa.get(chave) ?? 0 });
      mesAtual -= 1;
      if (mesAtual === 0) {
        mesAtual = 12;
        anoAtual -= 1;
      }
    }
    return resultado;
  }, [itens, visaoEnvios, intervalo]);

  // ---------------------------------------------------------------------------
  // Propostas por vendedor (filtrado por mês) — só faz sentido para quem vê todas
  // ---------------------------------------------------------------------------

  const propostasPorVendedor = useMemo(() => {
    if (!mesFiltroVendedor) return [];

    const mapa = new Map<string, number>();

    for (const item of itens) {
      if (item.emitidaEm.startsWith(mesFiltroVendedor)) {
        const nome = extrairNome(item.responsavelEmail);
        mapa.set(nome, (mapa.get(nome) ?? 0) + 1);
      }
    }

    return [...mapa.entries()]
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => b.quantidade - a.quantidade);
  }, [itens, mesFiltroVendedor]);

  /**
   * Escala do gráfico de propostas por vendedor: a maior quantidade do mês.
   *
   * Derivado aqui, e não dentro do `map` do gráfico, para não recalcular o máximo a cada barra. E por
   * `Math.max`, e não pelo primeiro item da lista: ela chega ordenada por quantidade, então ler o
   * primeiro funcionaria por coincidência — trocar a ordenação para alfabética, um dia, produziria
   * barras acima de 100% sem erro algum.
   */
  const maxPropostasVendedor = useMemo(
    () => Math.max(0, ...propostasPorVendedor.map((v) => v.quantidade)),
    [propostasPorVendedor],
  );

  // ---------------------------------------------------------------------------
  // Acompanhamento da carteira (vendedor sem ver_todas)
  // ---------------------------------------------------------------------------

  /**
   * Usa `todosItens` filtrado por `comoUsuario` (quando ativo), **NÃO** `itens`.
   *
   * ## Por quê
   *
   * `itens` já passou pelo filtro de período global, que recorta por DATA DE EMISSÃO. Este bloco
   * trata de VIGÊNCIA — propostas que vencem ou venceram recentemente. Se usasse `itens`, uma
   * proposta emitida em maio que vence semana que vem sumiria quando o período fosse "este
   * trimestre", escondendo justamente o que exige ação urgente.
   */
  const dadosCarteira: readonly GrupoFaixa[] = useMemo(() => {
    if (veTodas) return [];
    const sub = sessao?.sub ?? '';
    // Filtra por comoUsuario (para admin simulando) ou pelo sub do próprio vendedor
    const base =
      comoUsuario !== ''
        ? todosItens.filter((i) => i.responsavelSub === comoUsuario)
        : todosItens.filter((i) => i.responsavelSub === sub);
    return agruparAcompanhamento(base, new Date());
  }, [veTodas, todosItens, comoUsuario, sessao]);

  /** Total de itens que exigem ação — soma todas as faixas. */
  const totalCarteira = useMemo(
    () => dadosCarteira.reduce((acc, g) => acc + g.itens.length, 0),
    [dadosCarteira],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (carregando && todosItens.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-gray-500 dark:text-gray-400">Carregando dashboard...</p>
      </div>
    );
  }

  if (erro !== null) {
    return (
      <div role="alert" className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4">
        <p className="text-sm font-semibold text-red-800">Não foi possível carregar</p>
        <p className="mt-1 text-sm text-red-700">{erro}</p>
        <button
          type="button"
          onClick={() => void carregar()}
          className="mt-3 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (todosItens.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-12 text-center">
        <p className="font-semibold text-brand-blue dark:text-white">
          Nenhuma proposta publicada ainda
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Os gráficos aparecerão quando houver propostas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho com título, meta e seletor de período global à direita */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-bold text-brand-blue dark:text-white sm:text-2xl">
            Visão geral das propostas comerciais
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intervalo !== null
              ? `${String(itens.length)} de ${String(todosItens.length)} propostas`
              : `${String(itens.length)} propostas`}
            {atualizadoEm !== null &&
              ` · atualizado às ${atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Botões de atalho Q1–Q4 do ano corrente */}
          <div
            className="flex items-center gap-0.5 sm:gap-1"
            role="group"
            aria-label="Atalhos de trimestre"
          >
            {([1, 2, 3, 4] as const).map((n) => {
              const chave = chaveTrimestre(anoCorrente, n);
              const futuro = intervaloDoTrimestre(chave, new Date()) === null;
              const ativo = tipoPeriodo === 'trimestre' && trimAtivo === chave;

              return (
                <button
                  key={chave}
                  type="button"
                  disabled={futuro}
                  title={
                    futuro
                      ? `Q${String(n)} ainda não começou`
                      : `Trimestre ${String(n)} de ${String(anoCorrente)}`
                  }
                  aria-pressed={ativo}
                  onClick={() => {
                    setTipoPeriodo('trimestre');
                    setTrimestreSelecionado(chave);
                    setVisaoEnvios(GRANULARIDADE_SUGERIDA.trimestre);
                  }}
                  className={[
                    'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                    ativo
                      ? 'bg-brand-blue text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600',
                    futuro ? 'opacity-50 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  Q{String(n)}
                </button>
              );
            })}
          </div>

          {/* Seletor de período global */}
          <label
            htmlFor="periodo-dashboard"
            className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300"
          >
            Período
            <select
              id="periodo-dashboard"
              value={tipoPeriodo}
              onChange={(e) => {
                const novo = e.target.value as TipoPeriodo;
                setTipoPeriodo(novo);
                setVisaoEnvios(GRANULARIDADE_SUGERIDA[novo]);
              }}
              className="rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent"
            >
              {OPCOES_PERIODO.map((op) => (
                <option key={op} value={op}>
                  {ROTULOS_PERIODO[op]}
                </option>
              ))}
            </select>
          </label>

          {/* Seletor secundário de trimestre */}
          {tipoPeriodo === 'trimestre' && (
            <label
              htmlFor="trimestre-dashboard"
              className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300"
            >
              Trimestre
              <select
                id="trimestre-dashboard"
                value={
                  trimestreSelecionado === '' ? trimestreCorrente(new Date()) : trimestreSelecionado
                }
                onChange={(e) => {
                  setTrimestreSelecionado(e.target.value);
                }}
                className="rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent"
              >
                {opcoesTrimestreDisponiveis.map((chave) => (
                  <option key={chave} value={chave}>
                    {rotuloTrimestre(chave, new Date())}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Inputs de data quando período é personalizado */}
          {tipoPeriodo === 'personalizado' && (
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="periodo-de"
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
              >
                De
                <input
                  id="periodo-de"
                  type="date"
                  value={dataDe}
                  max={dataAte !== '' ? dataAte : hojeIso}
                  onChange={(e) => {
                    setDataDe(e.target.value);
                  }}
                  className="rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent"
                />
              </label>
              <label
                htmlFor="periodo-ate"
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
              >
                Até
                <input
                  id="periodo-ate"
                  type="date"
                  value={dataAte}
                  min={dataDe}
                  max={hojeIso}
                  onChange={(e) => {
                    setDataAte(e.target.value);
                  }}
                  className="rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent"
                />
              </label>
            </div>
          )}

          {veTodas && (
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              Visualizar como
              <select
                value={comoUsuario}
                onChange={(e) => {
                  setComoUsuario(e.target.value);
                }}
                className="rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-accent"
              >
                <option value="">Todos os vendedores</option>
                {responsaveis.map((r) => (
                  <option key={r.sub} value={r.sub}>
                    {r.nome}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            disabled={carregando}
            onClick={() => {
              void carregar();
            }}
            className="shrink-0 rounded-lg bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-blue/90 disabled:opacity-50 dark:bg-brand-accent dark:hover:bg-brand-accent/90"
          >
            {carregando ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* Aviso de período invertido */}
      {periodoInvertido && (
        <div
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
        >
          A data inicial é posterior à data final. Ajuste para ver os resultados corretos.
        </div>
      )}

      {comoUsuario !== '' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
          Visualizando como {responsaveis.find((r) => r.sub === comoUsuario)?.nome ?? comoUsuario}.
          Esta é exatamente a lista que essa pessoa vê.
        </div>
      )}

      {/* Legenda do período ativo */}
      <p className="text-sm font-medium text-gray-600 dark:text-gray-300 break-words">
        Dados de {descricaoPeriodo}
      </p>

      {/* Aviso de trimestre futuro */}
      {trimestreFuturo ? (
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-12 text-center">
          <p className="font-semibold text-brand-blue dark:text-white">
            Trimestre ainda não começou
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 break-words">
            O trimestre selecionado está no futuro. Os dados aparecerão quando ele iniciar.
          </p>
          <button
            type="button"
            onClick={() => {
              setTrimestreSelecionado('');
              setVisaoEnvios(GRANULARIDADE_SUGERIDA.trimestre);
            }}
            className="mt-4 rounded-lg bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-blue/90 dark:bg-brand-accent dark:hover:bg-brand-accent/90"
          >
            Voltar ao trimestre atual
          </button>
        </div>
      ) : itens.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-12 text-center">
          <p className="font-semibold text-brand-blue dark:text-white">
            Nenhuma proposta emitida neste período
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 break-words">
            Não há propostas no recorte de {descricaoPeriodo}. Tente ampliar o intervalo.
          </p>
          <button
            type="button"
            onClick={() => {
              setTipoPeriodo('tudo');
              setVisaoEnvios(GRANULARIDADE_SUGERIDA.tudo);
            }}
            className="mt-4 rounded-lg bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-blue/90 dark:bg-brand-accent dark:hover:bg-brand-accent/90"
          >
            Ver todo o histórico
          </button>
        </div>
      ) : (
        <>
          {/* Cards de métricas */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Total de Propostas
              </p>
              <p className="mt-1 text-xl font-bold text-brand-blue dark:text-white sm:text-2xl">
                {String(metricas.total)}
              </p>
              {metricas.legadoCount > 0 && (
                <p className="mt-0.5 text-xs text-gray-500">
                  {String(metricas.legadoCount)} do construtor antigo
                </p>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Recorrência Mensal Total
              </p>
              {metricas.comValor > 0 ? (
                <>
                  <p className="mt-1 text-xl font-bold text-brand-blue dark:text-white sm:text-2xl">
                    {formatarUsd(metricas.volumeTotal)}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    sem crédito, de {String(metricas.comValor)}{' '}
                    {metricas.comValor === 1 ? 'proposta' : 'propostas'} com valor
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-1 text-xl font-bold text-gray-300 dark:text-gray-600 sm:text-2xl">
                    &mdash;
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">Sem proposta com valor conhecido</p>
                </>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Ticket Médio
              </p>
              {metricas.comValor > 0 ? (
                <p className="mt-1 text-xl font-bold text-brand-blue dark:text-white sm:text-2xl">
                  {formatarUsd(metricas.ticketMedio)}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-xl font-bold text-gray-300 dark:text-gray-600 sm:text-2xl">
                    &mdash;
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">Depende de proposta com valor</p>
                </>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Créditos AWS Pleiteados
              </p>
              {metricas.comValor > 0 ? (
                <p className="mt-1 text-xl font-bold text-brand-accent sm:text-2xl">
                  {formatarUsd(metricas.creditosTotal)}
                </p>
              ) : (
                <p className="mt-1 text-xl font-bold text-gray-300 dark:text-gray-600 sm:text-2xl">
                  &mdash;
                </p>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Maior Proposta
              </p>
              {metricas.comValor > 0 ? (
                <p className="mt-1 text-xl font-bold text-brand-blue dark:text-white sm:text-2xl">
                  {formatarUsd(metricas.maiorMensalidade)}
                </p>
              ) : (
                <p className="mt-1 text-xl font-bold text-gray-300 dark:text-gray-600 sm:text-2xl">
                  &mdash;
                </p>
              )}
            </div>
          </div>

          {/* Gráfico de barras horizontal — top 5 */}
          {top5.length > 3 && (
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Top 5 por recorrência mensal (sem crédito)
              </p>
              <div className="space-y-1">
                {top5.map((entry, idx) => {
                  const max = top5[0]?.valor ?? 1;
                  const pct = max > 0 ? (entry.valor / max) * 100 : 0;
                  const expandido = top5Expandido === entry.item.numero;

                  return (
                    <div key={entry.item.numero}>
                      <div
                        className="flex cursor-pointer items-center gap-3 rounded-lg p-1 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                        role="button"
                        tabIndex={0}
                        aria-expanded={expandido}
                        onClick={() => {
                          setTop5Expandido(expandido ? null : entry.item.numero);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setTop5Expandido(expandido ? null : entry.item.numero);
                          }
                        }}
                      >
                        {/* Chevron */}
                        <svg
                          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${expandido ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>

                        <span className="w-20 shrink-0 truncate text-xs font-medium text-gray-700 dark:text-gray-200 sm:w-32">
                          {entry.item.cliente}
                        </span>
                        <div className="relative h-5 flex-1 rounded bg-gray-100 dark:bg-gray-700">
                          <div
                            className="h-5 rounded transition-all"
                            style={{
                              width: `${String(pct)}%`,
                              backgroundColor: CORES_GRAFICO[idx % CORES_GRAFICO.length],
                            }}
                          />
                        </div>
                        <span className="w-16 shrink-0 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 sm:w-28">
                          {formatarUsd(entry.valor)}
                        </span>
                      </div>

                      {/* Painel expandido com detalhes */}
                      {expandido && entry.item.financeiro !== null && (
                        <div className="ml-7 mt-1 mb-2 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-6 py-4">
                          <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Cliente
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.cliente}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Projeto
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.projeto}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Número
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.numero}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Plano de Suporte
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.financeiro.planoSuporte}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Trilha
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.financeiro.trilha}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Consumo AWS
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {formatarUsd(entry.item.financeiro.consumoUsd)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Suporte
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {formatarUsd(entry.item.financeiro.suporteUsd)}
                              </p>
                            </div>
                            {entry.item.financeiro.creditoUsd > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Crédito AWS
                                </p>
                                <p className="text-sm font-semibold text-brand-accent">
                                  {formatarUsd(entry.item.financeiro.creditoUsd)}
                                </p>
                              </div>
                            )}
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Economia
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.financeiro.economiaPercentual.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 1,
                                  maximumFractionDigits: 1,
                                })}
                                %
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Validade
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {entry.item.validaAte.split('-').reverse().join('/')}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 flex items-center justify-between rounded-lg bg-brand-blue/5 dark:bg-brand-blue/20 px-4 py-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Recorrência mensal
                              </p>
                              <p className="text-lg font-bold text-brand-blue dark:text-white">
                                {formatarUsd(entry.item.financeiro.mensalidadeSemCreditoUsd)}
                              </p>
                              {/*
                               * O valor faturado só aparece quando difere, e é o que explica o tamanho
                               * da barra: sem isto, uma proposta cujo crédito zera a parcela lidera o
                               * ranking exibindo um valor que ninguém reconhece.
                               */}
                              {entry.item.financeiro.mensalidadeSemCreditoUsd -
                                entry.item.financeiro.mensalidadeUsd >
                                0.01 && (
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  {formatarUsd(entry.item.financeiro.mensalidadeUsd)} faturado na
                                  janela do crédito
                                </p>
                              )}
                            </div>
                            <a
                              href={entry.item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-brand-accent text-white px-4 py-2 rounded-lg text-sm font-semibold"
                            >
                              Abrir proposta
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {veTodas && rankVendedores.length > 0 && (
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Rank de vendedores
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-200 dark:border-gray-700 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        #
                      </th>
                      <th scope="col" className="px-3 py-2 font-semibold">
                        Vendedor
                      </th>
                      <th scope="col" className="px-3 py-2 text-right font-semibold">
                        Propostas
                      </th>
                      <th scope="col" className="px-3 py-2 text-right font-semibold">
                        Recorrência Total
                      </th>
                      <th scope="col" className="px-3 py-2 text-right font-semibold">
                        Ticket Médio
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                    {rankVendedores.map((v, idx) => (
                      <tr key={v.nome} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 py-2 font-bold text-brand-accent">
                          {String(idx + 1)}º
                        </td>
                        <td className="px-3 py-2 font-medium text-brand-blue dark:text-white">
                          {v.nome}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">
                          {String(v.quantidade)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-gray-200">
                          {formatarUsd(v.volume)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-200">
                          {formatarUsd(v.ticketMedio)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Acompanhamento da carteira — substitui o rank para vendedores sem ver_todas */}
          {!veTodas && (
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Acompanhamento da sua carteira
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                  Baseado na data de validade · ignora o filtro de período
                </p>
              </div>

              {/* Tira de contagem */}
              <div
                className="mb-4 flex flex-wrap gap-2"
                role="group"
                aria-label="Filtrar por faixa de urgência"
              >
                {FAIXAS.map((faixa) => {
                  const grupo = dadosCarteira.find((g) => g.faixa.id === faixa.id);
                  const quantidade = grupo?.itens.length ?? 0;
                  const ativo = filtroFaixaCarteira === faixa.id;
                  const vazio = quantidade === 0;

                  const coresBotao: Record<string, string> = {
                    vermelho: ativo
                      ? 'bg-red-600 text-white border-red-600'
                      : 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30',
                    ambar: ativo
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30',
                    azul: ativo
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30',
                    cinza: ativo
                      ? 'bg-gray-600 text-white border-gray-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                  };

                  return (
                    <button
                      key={faixa.id}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => {
                        setFiltroFaixaCarteira(ativo ? null : faixa.id);
                      }}
                      className={[
                        'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                        coresBotao[faixa.cor] ?? '',
                        vazio ? 'opacity-40 cursor-default' : 'cursor-pointer',
                      ].join(' ')}
                    >
                      <span className="mr-1">{String(quantidade)}</span>
                      <span>{faixa.rotulo}</span>
                    </button>
                  );
                })}
              </div>

              {/* Lista de propostas que exigem ação */}
              {totalCarteira === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-4 py-8 text-center">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    ✓ Nenhuma proposta exige ação nos próximos 30 dias
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Suas propostas estão com prazos confortáveis ou já foram encerradas.
                  </p>
                </div>
              ) : (
                (() => {
                  // Filtra por faixa selecionada ou mostra todas
                  const itensVisiveis = dadosCarteira
                    .filter(
                      (g) => filtroFaixaCarteira === null || g.faixa.id === filtroFaixaCarteira,
                    )
                    .flatMap((g) => g.itens);

                  const TETO = 15;
                  const exibidos = itensVisiveis.slice(0, TETO);
                  const excedente = itensVisiveis.length - TETO;

                  const coresSelo: Record<string, string> = {
                    vencendoJa: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
                    vencendoSemana:
                      'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200',
                    vencendoMes: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
                    vencidaRecente: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                  };

                  const rotulosCurtos: Record<string, string> = {
                    vencendoJa: 'Urgente',
                    vencendoSemana: 'Semana',
                    vencendoMes: '30 dias',
                    vencidaRecente: 'Vencida',
                  };

                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="border-b border-gray-200 dark:border-gray-700 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          <tr>
                            <th scope="col" className="px-2 py-2 font-semibold">
                              Urgência
                            </th>
                            <th scope="col" className="px-2 py-2 font-semibold">
                              Cliente
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-semibold hidden sm:table-cell"
                            >
                              Projeto
                            </th>
                            <th scope="col" className="px-2 py-2 font-semibold">
                              Validade
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-semibold hidden md:table-cell"
                            >
                              Prazo
                            </th>
                            {/*
                             * "Faturado", e não "Valor mensal": esta coluna mostra
                             * `mensalidadeUsd`, líquida do crédito, enquanto as métricas no topo
                             * da mesma tela agregam a recorrência. Dois números diferentes para a
                             * mesma proposta na mesma tela sob rótulos que não os distinguem
                             * seriam lidos como erro de cálculo.
                             *
                             * O faturado é o certo aqui: a coluna existe para a conversa de
                             * revalidação, e o número que o cliente reconhece é o que ele paga.
                             */}
                            <th
                              scope="col"
                              className="px-2 py-2 text-right font-semibold hidden lg:table-cell"
                              title="Mensalidade faturada durante a janela do crédito AWS. As métricas do topo usam a recorrência, sem o crédito."
                            >
                              Faturado/mês
                            </th>
                            <th scope="col" className="px-2 py-2 text-right font-semibold">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                          {exibidos.map((entrada) => (
                            <tr
                              key={`${entrada.item.numero}-${entrada.item.validaAte}`}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <td className="px-2 py-2">
                                <span
                                  className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${coresSelo[entrada.faixa] ?? ''}`}
                                >
                                  {rotulosCurtos[entrada.faixa] ?? ''}
                                </span>
                              </td>
                              <td className="px-2 py-2 font-medium text-gray-800 dark:text-gray-200 max-w-[140px] truncate">
                                {entrada.item.cliente}
                              </td>
                              <td className="px-2 py-2 text-gray-600 dark:text-gray-300 max-w-[120px] truncate hidden sm:table-cell">
                                {entrada.item.projeto}
                              </td>
                              <td className="px-2 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {entrada.item.validaAte.split('-').reverse().join('/')}
                              </td>
                              <td className="px-2 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap hidden md:table-cell">
                                {entrada.descricao}
                              </td>
                              <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-200 hidden lg:table-cell">
                                {entrada.item.financeiro !== null
                                  ? formatarUsd(entrada.item.financeiro.mensalidadeUsd)
                                  : '—'}
                              </td>
                              <td className="px-2 py-2 text-right whitespace-nowrap">
                                <a
                                  href={entrada.item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Abrir proposta de ${entrada.item.cliente}`}
                                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-brand-blue dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                >
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                    />
                                  </svg>
                                  Abrir
                                </a>
                                <button
                                  type="button"
                                  aria-label={`Copiar link da proposta de ${entrada.item.cliente}`}
                                  onClick={() => {
                                    void navigator.clipboard.writeText(entrada.item.url);
                                  }}
                                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
                                    />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {excedente > 0 && (
                        <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                          e mais {String(excedente)} {excedente === 1 ? 'proposta' : 'propostas'}
                        </p>
                      )}
                    </div>
                  );
                })()
              )}

              {/* Ação sugerida da faixa selecionada */}
              {filtroFaixaCarteira !== null && (
                <p className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-900 px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Sugestão:</span>{' '}
                  {FAIXAS.find((f) => f.id === filtroFaixaCarteira)?.acaoSugerida ?? ''}
                </p>
              )}
            </div>
          )}

          {/* Dois gráficos lado a lado */}
          <div
            className={`grid gap-3 sm:gap-4 ${veTodas ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}
          >
            {/* Gráfico de barras verticais — envios por período */}
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Propostas enviadas
                </p>
                <select
                  value={visaoEnvios}
                  onChange={(e) => {
                    setVisaoEnvios(e.target.value as VisaoEnvios);
                  }}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  aria-label="Tipo de visão"
                >
                  <option value="diaria">Visão Diária</option>
                  <option value="semanal">Visão Semanal</option>
                  <option value="mensal">Visão Mensal</option>
                  <option value="trimestral">Visão Trimestral</option>
                  <option value="anual">Visão Anual</option>
                </select>
              </div>

              {dadosEvolucao.length > 0 ? (
                <div className="flex items-end gap-px overflow-x-auto" style={{ height: '160px' }}>
                  {dadosEvolucao.map((d) => {
                    const max = Math.max(...dadosEvolucao.map((e) => e.quantidade));
                    const pct = max > 0 ? (d.quantidade / max) * 100 : 0;

                    return (
                      <div
                        key={d.chave}
                        className="flex min-w-[12px] flex-1 flex-col items-center justify-end gap-0.5 sm:min-w-[18px]"
                        style={{ height: '100%' }}
                      >
                        {d.quantidade > 0 && (
                          <span className="text-[10px] font-bold text-brand-blue dark:text-blue-400">
                            {String(d.quantidade)}
                          </span>
                        )}
                        <div
                          className="w-full max-w-[48px] rounded-t bg-brand-accent transition-all"
                          style={{
                            height: d.quantidade > 0 ? `${String(pct)}%` : '0px',
                            minHeight: d.quantidade > 0 ? '4px' : '1px',
                          }}
                        />
                        <span className="mt-0.5 max-w-[56px] truncate text-[9px] text-gray-500 dark:text-gray-400">
                          {d.rotulo}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="py-8 text-center text-xs text-gray-400">Sem dados de envio.</p>
              )}
            </div>

            {veTodas && (
              <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Propostas por Vendedor
                  </p>
                  <select
                    value={mesFiltroVendedor}
                    onChange={(e) => {
                      setMesFiltroVendedor(e.target.value);
                    }}
                    aria-label="Filtrar mês para propostas por vendedor"
                    className="ml-auto rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 text-xs"
                  >
                    {mesesDisponiveis.map((m) => {
                      const [ano, mes] = m.split('-');
                      const rotulo = `${MESES[parseInt(mes ?? '1', 10) - 1] ?? ''}/${ano ?? ''}`;
                      return (
                        <option key={m} value={m}>
                          {rotulo}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {propostasPorVendedor.length > 0 ? (
                  /*
                   * `items-stretch`, e não `items-end`: com `items-end` cada coluna encolhe para a
                   * altura do próprio conteúdo, a trilha `flex-1` fica com zero, e altura em
                   * porcentagem de zero é zero. Toda barra saía com os 4px do `minHeight`,
                   * independentemente do valor — 3, 2 e 2 viravam três traços idênticos e os pixels
                   * reservados para o gráfico ficavam vazios. O alinhamento pela base continua de pé:
                   * quem o faz é o `items-end` da trilha, uma camada abaixo.
                   */
                  <div className="flex h-64 items-stretch justify-around gap-3">
                    {propostasPorVendedor.map((v, i) => {
                      const altura =
                        maxPropostasVendedor > 0 ? (v.quantidade / maxPropostasVendedor) * 100 : 0;
                      const cor = CORES_GRAFICO[i % CORES_GRAFICO.length] ?? '#0A2540';

                      return (
                        <div key={v.nome} className="flex flex-1 flex-col items-center gap-2">
                          <span className="text-sm font-bold text-brand-blue dark:text-white">
                            {v.quantidade}
                          </span>
                          <div className="flex w-full flex-1 items-end">
                            <div
                              className="w-full rounded-t transition-all"
                              style={{
                                height: `${String(altura)}%`,
                                backgroundColor: cor,
                                minHeight: '4px',
                              }}
                              title={`${v.nome}: ${String(v.quantidade)}`}
                            />
                          </div>
                          <span
                            className="w-full truncate text-center text-[11px] text-gray-500 dark:text-gray-400"
                            title={v.nome}
                          >
                            {v.nome}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="py-8 text-center text-xs text-gray-400">
                    Nenhuma proposta neste mês.
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
